import React from 'react'
import { RouterProvider, createBrowserRouter, createMemoryRouter, Navigate } from 'react-router-dom'
import { useAuth } from '../providers/AuthProvider'
import { ProtectedRoute } from './ProtectedRoute'
import { UnprotectedRoute } from './UnprotectedRoute'
import { config } from '../config'
import PIRPage from '../components/pages/PIRPage'
import PaymentPage from '../components/payments/PaymentPage'
import PIRResponsePage from '../components/pages/PIRResponsePage'
import ResponseDownloadPage from '../components/pages/ResponseDownloadPage'
import NotFoundPage from '../components/pages/NotFoundPage'
import LoginPage from '../components/pages/LoginPage'
import LogoutPage from '../components/pages/LogoutPage'
import ForgotPasswordPage from '../components/pages/ForgotPasswordPage'
import PIRDashboardPage from '../components/pages/PIRDashboardPage'
import FOIDashboardPage from '../components/pages/FOIDashboardPage'
import TasksPage from '../components/pages/TasksPage'
import FOIReportingPage from '../components/pages/FOIReportingPage'
import FOIRequestPage from '../components/pages/FOIRequestPage'
import FOIResponsePage from '../components/pages/FOIResponsePage'
import BillReviewPage from '../components/pages/BillReviewPage'
import FOIPaymentPage from '../components/pages/FOIPaymentPage'
import RedactionEditorPage from '../components/records/redactionEditor/RedactionEditorPage'
import RedactionViewerPage from '../components/records/redactionEditor/RedactionViewerPage'
import { settingsRoutes } from './settingsRoutes'

export const Routes = ({ useMemory = false, initialEntries = ['/'] }) => {
  const { authState } = useAuth()

  const defaultAuthenticatedRoute = config.foi.active ? (
    <Navigate replace to="/foi/inprogress" />
  ) : config.pir.active ? (
    <Navigate replace to="/pir/dashboard" />
  ) : (
    <NotFoundPage />
  )

  const defaultNotAuthenticatedRoute = config.foi.active ? <FOIRequestPage /> : config.pir.active ? <PIRPage /> : <NotFoundPage />

  const foiRoutesAuthenticated = config.foi.active
    ? [
        { path: '/foi/create', element: <FOIRequestPage /> },
        { path: '/foi/tasks', element: <TasksPage /> },
        { path: '/foi/inprogress', element: <FOIDashboardPage isCompleted={false} key="/foi/inprogress" /> },
        { path: '/foi/completed', element: <FOIDashboardPage isCompleted key="/foi/completed" /> },
        { path: '/foi/reports', element: <FOIReportingPage /> },
        { path: '/foi/:foiId', element: <FOIResponsePage /> },
        { path: '/foi/:foiId/category/:categoryId/redactions', element: <RedactionEditorPage /> },
        { path: '/foi/:foiId/category/:categoryId/files/:fileId', element: <RedactionViewerPage /> },
        { path: '/foi/:foiId/payment/:billId', element: <FOIPaymentPage /> },
        { path: '/payment/bill/:billId', element: <FOIPaymentPage /> },
        { path: '/bill/:billId/review', element: <BillReviewPage /> },
        settingsRoutes
      ]
    : []

  const foiRoutesNotAuthenticated = config.foi.active
    ? [
        { path: '/foi/create', element: <FOIRequestPage /> },
        { path: '/foi/:foiId/payment/:billId', element: <FOIPaymentPage /> },
        { path: '/payment/bill/:billId', element: <FOIPaymentPage /> },
        { path: '/foi/:foiId/download', element: <ResponseDownloadPage /> },
        { path: '/bill/:billId/review', element: <BillReviewPage /> }
      ]
    : []

  const pirRoutesAuthenticated = config.pir.active
    ? [
        { path: '/pir/dashboard', element: <PIRDashboardPage /> },
        { path: '/pir/:pirId', element: <PIRResponsePage /> },
        { path: '/payment/:pirId', element: <PaymentPage /> }
      ]
    : []

  const pirRoutesNotAuthenticated = config.pir.active
    ? [
        { path: '/pir/create', element: <PIRPage /> },
        { path: '/payment/:pirId', element: <PaymentPage /> },
        { path: '/pir/:pirId/download', element: <ResponseDownloadPage /> }
      ]
    : []

  const citadelRoutesAuthenticated = [
    { path: '/login', element: <Navigate replace to="/" /> },
    { path: '/logout', element: <LogoutPage /> }
  ]

  const citadelRoutesNotAuthenticated = [
    { path: '/login', element: <LoginPage /> },
    { path: '/resetpassword', element: <ForgotPasswordPage /> }
  ]

  const routesForAuthenticatedOnly = [
    {
      path: '/',
      element: <ProtectedRoute />,
      children: [
        ...foiRoutesAuthenticated,
        ...pirRoutesAuthenticated,
        ...citadelRoutesAuthenticated,
        { path: '/', element: defaultAuthenticatedRoute },
        { path: '*', element: <NotFoundPage /> }
      ]
    }
  ]

  const routesForNotAuthenticatedOnly = [
    {
      path: '/',
      element: <UnprotectedRoute />,
      children: [
        ...foiRoutesNotAuthenticated,
        ...pirRoutesNotAuthenticated,
        ...citadelRoutesNotAuthenticated,
        { path: '/', element: defaultNotAuthenticatedRoute }
      ]
    }
  ]

  const routes = [...(!authState.isLoggedIn ? routesForNotAuthenticatedOnly : []), ...routesForAuthenticatedOnly]

  const router = useMemory ? createMemoryRouter(routes, { initialEntries }) : createBrowserRouter(routes)

  return <RouterProvider router={router} />
}
